// Create grid modal — all fields from legacy showCreateModal / createGrid().
import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { API_BASE, apiPost } from "@/lib/api-client";
import Modal from "@/components/common/modal";
import type { GridSpacing } from "@/types/api";

interface PreCreateAdvice {
  ok: boolean;
  conf: number;
  regime: "T" | "R" | "V" | "Q";
  why: string;
  risks: string[];
  params: Record<string, any> | null;
}
const REGIME_LABEL: Record<string, string> = { T: "trending", R: "ranging", V: "volatile", Q: "quiet" };

interface CreateGridBody {
  symbol: string;
  lower_price: number;
  upper_price: number;
  grid_count: number;
  quantity_per_grid: number;
  leverage: number;
  side: string;
  trailing: boolean;
  spacing: GridSpacing;
  atr_multiplier?: number;
  max_hold_minutes: number | null;
  force_exit_minutes: number | null;
  max_deviation_pct: number | null;
  ai_enabled: boolean;
  ai_auto_act: boolean;
}

export interface CreateGridInitialValues {
  symbol?: string;
  lower_price?: number;
  upper_price?: number;
  grid_count?: number;
  quantity_per_grid?: number;
  leverage?: number;
  side?: string;
  trailing?: boolean;
  spacing?: GridSpacing;
  atr_multiplier?: number | null;
  max_hold_minutes?: number | null;
  force_exit_minutes?: number | null;
  max_deviation_pct?: number | null;
}

interface CreateGridModalProps {
  onClose: () => void;
  initial?: CreateGridInitialValues;
}

const inputCls =
  "w-full bg-bg border border-border rounded px-2.5 py-1.5 text-sm text-text outline-none focus:border-accent/60 transition-colors";
const primaryBtn =
  "text-xs px-4 py-2 rounded bg-accent/20 border border-accent/50 text-accent hover:bg-accent/30 transition-colors disabled:opacity-50";
const ghostBtn =
  "text-xs px-4 py-2 rounded border border-border text-muted hover:text-text transition-colors";

function Field({ label, hint, children }: { label: string; hint?: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[0.7rem] uppercase tracking-wider text-muted mb-1">{label}</label>
      {children}
      {hint && <div className="text-[0.65rem] text-muted mt-1">{hint}</div>}
    </div>
  );
}

export default function CreateGridModal({ onClose, initial }: CreateGridModalProps) {
  const qc = useQueryClient();
  const navigate = useNavigate();

  // Mode: auto (±% around current) or manual (explicit lower/upper)
  const [mode, setMode] = useState<"auto" | "manual">(
    initial?.lower_price != null ? "manual" : "auto"
  );
  const [symbol, setSymbol] = useState(initial?.symbol ?? "SOL/USDT:USDT");
  const [interval, setInterval] = useState("0.4");
  const [lower, setLower] = useState(initial?.lower_price != null ? String(initial.lower_price) : "");
  const [upper, setUpper] = useState(initial?.upper_price != null ? String(initial.upper_price) : "");
  const [gridCount, setGridCount] = useState(String(initial?.grid_count ?? 5));
  const [qty, setQty] = useState(String(initial?.quantity_per_grid ?? 0.01));
  const [leverage, setLeverage] = useState(String(initial?.leverage ?? 1));
  const [side, setSide] = useState(initial?.side ?? "neutral");
  const [trailing, setTrailing] = useState(initial?.trailing ?? true);
  const [spacing, setSpacing] = useState<GridSpacing>(initial?.spacing ?? "geometric");
  const [atrMult, setAtrMult] = useState(
    initial?.atr_multiplier != null ? String(initial.atr_multiplier) : "1.5"
  );
  const [maxHold, setMaxHold] = useState(
    initial?.max_hold_minutes != null ? String(initial.max_hold_minutes) : "5"
  );
  const [forceExit, setForceExit] = useState(
    initial?.force_exit_minutes != null ? String(initial.force_exit_minutes) : "60"
  );
  const [maxDev, setMaxDev] = useState(
    initial?.max_deviation_pct != null ? String(initial.max_deviation_pct) : ""
  );
  const [error, setError] = useState("");
  const [aiAdvice, setAiAdvice] = useState<PreCreateAdvice | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  // AI advisor flags — mirror wind modal. When enabled, runtime monitor +
  // pre-placement gate consult AI; auto-act gives AI permission to execute
  // PAUSE/RESUME/CUT + skip placement automatically.
  const [aiEnabled, setAiEnabled] = useState(false);
  const [aiAutoAct, setAiAutoAct] = useState(false);

  const mutation = useMutation({
    mutationFn: (body: CreateGridBody) =>
      apiPost<{ grid_id?: string }>("/grids", body),
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["grids"] });
      onClose();
      if (data?.grid_id) {
        navigate(`/grid/${data.grid_id}`);
      }
    },
    onError: (e: Error) => setError(e.message),
  });

  /** Build the CreateGridBody from current form state, or throw a readable error. */
  async function buildBody(): Promise<CreateGridBody> {
    const count = parseInt(gridCount);
    const quantity = parseFloat(qty);
    const lev = parseInt(leverage);

    if (!symbol || !count || !quantity) throw new Error("Fill all required fields");

    let lowerPrice: number;
    let upperPrice: number;

    if (spacing === "dynamic") {
      lowerPrice = 0;
      upperPrice = 0;
    } else if (mode === "auto") {
      const pct = parseFloat(interval);
      if (!pct) throw new Error("Enter interval %");
      if (pct < 0.15) throw new Error("Interval < 0.15% will lose money to fees");

      // Fetch current price from OKX public API
      let price = 0;
      try {
        const instId = symbol.replace("/", "-").replace(":USDT", "-SWAP");
        const res = await fetch(
          `https://www.okx.com/api/v5/market/ticker?instId=${instId}`
        );
        const json = await res.json() as { data: { last: string }[] };
        price = parseFloat(json.data?.[0]?.last ?? "0");
      } catch {
        throw new Error("Could not fetch price for " + symbol);
      }
      if (!price) throw new Error("Could not fetch price for " + symbol);

      const halfRangePct = (pct * count) / 2 / 100;
      // Use enough decimals so low-priced assets (e.g. DOGE $0.15) don't round to same value
      const decimals = price >= 100 ? 2 : price >= 1 ? 4 : price >= 0.01 ? 6 : 8;
      lowerPrice = +(price * (1 - halfRangePct)).toFixed(decimals);
      upperPrice = +(price * (1 + halfRangePct)).toFixed(decimals);
    } else {
      lowerPrice = parseFloat(lower);
      upperPrice = parseFloat(upper);
      if (!lowerPrice || !upperPrice) throw new Error("Fill price range");
    }

    const body: CreateGridBody = {
      symbol,
      lower_price: lowerPrice,
      upper_price: upperPrice,
      grid_count: count,
      quantity_per_grid: quantity,
      leverage: lev,
      side,
      trailing,
      spacing,
      max_hold_minutes: maxHold ? parseInt(maxHold) : null,
      force_exit_minutes: forceExit ? parseInt(forceExit) : null,
      max_deviation_pct: maxDev ? parseFloat(maxDev) : null,
      ai_enabled: aiEnabled,
      ai_auto_act: aiAutoAct,
    };
    if (spacing === "dynamic") body.atr_multiplier = parseFloat(atrMult) || 1.5;
    return body;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    try {
      mutation.mutate(await buildBody());
    } catch (e) { setError((e as Error).message); }
  }

  async function handleReviewWithAi() {
    setError("");
    setAiAdvice(null);
    setAiLoading(true);
    try {
      const body = await buildBody();
      const res = await fetch(`${API_BASE}/grids/ai-pre-create`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(body),
      });
      if (res.status === 503) { setError("AI not configured on server"); return; }
      if (!res.ok) {
        const b = await res.json().catch(() => ({})) as { error?: string };
        throw new Error(b.error ?? res.statusText);
      }
      setAiAdvice(await res.json() as PreCreateAdvice);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setAiLoading(false);
    }
  }

  /** Apply AI's suggested params back into the form. User still clicks Create. */
  function applyAiParams() {
    if (!aiAdvice?.params) return;
    const p = aiAdvice.params;
    if (typeof p.lower === "number" && p.lower > 0) { setLower(String(p.lower)); setMode("manual"); }
    if (typeof p.upper === "number" && p.upper > 0) { setUpper(String(p.upper)); setMode("manual"); }
    if (typeof p.grid_count === "number" && p.grid_count > 0) setGridCount(String(p.grid_count));
    if (p.spacing === "arithmetic" || p.spacing === "geometric") setSpacing(p.spacing);
    if (typeof p.max_hold_minutes === "number" && p.max_hold_minutes >= 0) setMaxHold(String(p.max_hold_minutes));
    if (typeof p.force_exit_minutes === "number" && p.force_exit_minutes >= 0) setForceExit(String(p.force_exit_minutes));
    if (typeof p.leverage === "number" && p.leverage > 0) setLeverage(String(p.leverage));
    setAiAdvice(null);
  }

  return (
    <Modal open onClose={onClose} title="New Grid">
      <form onSubmit={handleSubmit} className="space-y-3">
        <Field label="Mode">
          <select
            value={mode}
            onChange={(e) => setMode(e.target.value as "auto" | "manual")}
            className={inputCls}
          >
            <option value="auto">Auto (±% around current price)</option>
            <option value="manual">Manual (set lower/upper)</option>
          </select>
        </Field>

        <Field label="Symbol">
          <input
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            placeholder="SOL/USDT:USDT"
            className={inputCls}
            required
          />
        </Field>

        {mode === "auto" && spacing !== "dynamic" && (
          <Field
            label="Interval % per level"
            hint="Min 0.15% to cover fees. 0.4% = safe default"
          >
            <input
              type="number"
              step="any"
              value={interval}
              onChange={(e) => setInterval(e.target.value)}
              className={inputCls}
            />
          </Field>
        )}

        {mode === "manual" && spacing !== "dynamic" && (
          <div className="grid grid-cols-2 gap-3">
            <Field label="Lower Price">
              <input
                type="number"
                step="any"
                value={lower}
                onChange={(e) => setLower(e.target.value)}
                placeholder="60000"
                className={inputCls}
              />
            </Field>
            <Field label="Upper Price">
              <input
                type="number"
                step="any"
                value={upper}
                onChange={(e) => setUpper(e.target.value)}
                placeholder="70000"
                className={inputCls}
              />
            </Field>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Grid Count">
            <input
              type="number"
              value={gridCount}
              onChange={(e) => setGridCount(e.target.value)}
              className={inputCls}
              required
            />
          </Field>
          <Field label="Qty per Grid">
            <input
              type="number"
              step="any"
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className={inputCls}
              required
            />
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Leverage">
            <input
              type="number"
              value={leverage}
              onChange={(e) => setLeverage(e.target.value)}
              className={inputCls}
              required
            />
          </Field>
          <Field label="Side">
            <select value={side} onChange={(e) => setSide(e.target.value)} className={inputCls}>
              <option value="neutral">Neutral</option>
              <option value="long">Long</option>
              <option value="short">Short</option>
            </select>
          </Field>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Field label="Spacing">
            <select
              value={spacing}
              onChange={(e) => setSpacing(e.target.value as GridSpacing)}
              className={inputCls}
            >
              <option value="geometric">Geometric (uniform %)</option>
              <option value="arithmetic">Arithmetic (uniform $)</option>
              <option value="dynamic">Dynamic (ATR-based)</option>
            </select>
          </Field>
          <Field label=" ">
            <label className="flex items-center gap-2 cursor-pointer mt-2">
              <input
                type="checkbox"
                checked={trailing}
                onChange={(e) => setTrailing(e.target.checked)}
                className="accent-accent"
              />
              <span className="text-sm text-dim">Trailing</span>
            </label>
          </Field>
        </div>

        {spacing === "dynamic" && (
          <Field
            label="ATR Multiplier"
            hint="interval% = ATR/price × multiplier. Default 1.5. Higher = wider grid."
          >
            <input
              type="number"
              step="0.1"
              value={atrMult}
              onChange={(e) => setAtrMult(e.target.value)}
              className={inputCls}
            />
          </Field>
        )}

        <div className="grid grid-cols-2 gap-3">
          <Field label="Break-even exit (min)">
            <input
              type="number"
              min="0"
              step="1"
              value={maxHold}
              onChange={(e) => setMaxHold(e.target.value)}
              placeholder="blank = off"
              className={inputCls}
            />
          </Field>
          <Field label="Force exit (min)">
            <input
              type="number"
              min="0"
              step="1"
              value={forceExit}
              onChange={(e) => setForceExit(e.target.value)}
              placeholder="blank = off"
              className={inputCls}
            />
          </Field>
        </div>

        <Field label="Max deviation stop (%)">
          <input
            type="number"
            min="0"
            step="0.01"
            value={maxDev}
            onChange={(e) => setMaxDev(e.target.value)}
            placeholder="e.g. 0.6 = cut at 0.6% adverse"
            className={inputCls}
          />
        </Field>

        {/* AI settings — mirror wind modal layout (stacked + inline hints).
            When AI disabled, auto-act is force-unchecked + disabled so the
            two flags can't get into a stale (enabled=0 && auto_act=1) state. */}
        <div className="space-y-2 pt-2 border-t border-border">
          <label className="flex items-start gap-2 cursor-pointer text-sm text-dim">
            <input
              type="checkbox"
              checked={aiEnabled}
              onChange={(e) => {
                setAiEnabled(e.target.checked);
                if (!e.target.checked) setAiAutoAct(false);
              }}
              className="accent-accent mt-0.5"
            />
            <span className="flex-1">
              <span className="font-medium text-text">Enable AI advisor</span>
              <span className="block text-[0.72rem] text-muted">
                Runtime monitor + pre-placement AI. Advice surfaces in UI (advisory only).
              </span>
            </span>
          </label>
          <label
            className={`flex items-start gap-2 text-sm text-dim ${
              aiEnabled ? "cursor-pointer" : "opacity-50 cursor-not-allowed"
            }`}
          >
            <input
              type="checkbox"
              checked={aiAutoAct}
              onChange={(e) => setAiAutoAct(e.target.checked)}
              className="accent-accent mt-0.5"
              disabled={!aiEnabled}
            />
            <span className="flex-1">
              <span className="font-medium text-text">AI auto-act</span>
              <span className="block text-[0.72rem] text-muted">
                AI automatically executes PAUSE / RESUME / CUT + skips placement in hostile regime.
              </span>
            </span>
          </label>
          {aiAutoAct && aiEnabled && (
            <div className="text-[0.72rem] text-warn bg-warn/10 rounded px-2 py-1.5">
              <span className="opacity-85 mr-1">⚠</span>
              AI will execute decisions automatically — verify conf thresholds before live use.
            </div>
          )}
        </div>

        {error && <div className="text-danger text-xs">{error}</div>}

        {aiAdvice && (
          <div className={`rounded border px-3 py-2 space-y-1.5 text-xs ${aiAdvice.ok ? "border-success/40 bg-success/5" : "border-warn/40 bg-warn/5"}`}>
            <div className="flex items-center gap-2">
              <span className={`font-semibold ${aiAdvice.ok ? "text-success" : "text-warn"}`}>
                {aiAdvice.ok ? "OK to proceed" : "Reconsider"}
              </span>
              <span className="text-muted">conf {Math.round(aiAdvice.conf * 100)}% · {REGIME_LABEL[aiAdvice.regime] ?? aiAdvice.regime}</span>
            </div>
            {aiAdvice.why && <div className="text-dim">{aiAdvice.why}</div>}
            {aiAdvice.risks?.length > 0 && (
              <ul className="text-warn space-y-0.5">
                {aiAdvice.risks.map((r, i) => <li key={i}>⚠ {r}</li>)}
              </ul>
            )}
            {aiAdvice.params && (() => {
              const p = aiAdvice.params;
              const rows: { label: string; val: string; was: string }[] = [];
              if (typeof p.lower === "number" && p.lower > 0)
                rows.push({ label: "Lower", val: String(p.lower), was: lower || "(auto)" });
              if (typeof p.upper === "number" && p.upper > 0)
                rows.push({ label: "Upper", val: String(p.upper), was: upper || "(auto)" });
              if (typeof p.grid_count === "number" && p.grid_count > 0)
                rows.push({ label: "Count", val: String(p.grid_count), was: gridCount });
              if (p.spacing === "arithmetic" || p.spacing === "geometric")
                rows.push({ label: "Spacing", val: p.spacing, was: spacing });
              if (typeof p.max_hold_minutes === "number" && p.max_hold_minutes >= 0)
                rows.push({ label: "Hold", val: `${p.max_hold_minutes}m`, was: `${maxHold}m` });
              if (typeof p.force_exit_minutes === "number" && p.force_exit_minutes >= 0)
                rows.push({ label: "Force", val: `${p.force_exit_minutes}m`, was: `${forceExit}m` });
              if (typeof p.leverage === "number" && p.leverage > 0)
                rows.push({ label: "Leverage", val: `${p.leverage}x`, was: `${leverage}x` });
              if (rows.length === 0) return null;
              return (
                <div className="flex flex-col gap-1 pt-1 border-t border-border/40">
                  <div className="text-[0.68rem] text-muted uppercase tracking-wider">Suggested params</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[0.75rem]">
                    {rows.map((r, i) => (
                      <span key={i}>
                        {r.label} <span className="text-accent">{r.val}</span>{" "}
                        <span className="text-dim">(was {r.was})</span>
                      </span>
                    ))}
                  </div>
                  <button type="button" onClick={applyAiParams} className={`${ghostBtn} !text-xs !py-1 self-start mt-0.5`}>
                    Apply all
                  </button>
                </div>
              );
            })()}
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className={ghostBtn}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleReviewWithAi}
            disabled={aiLoading || mutation.isPending}
            className={ghostBtn}
          >
            {aiLoading ? "Reviewing…" : "Review with AI"}
          </button>
          <button type="submit" disabled={mutation.isPending} className={primaryBtn}>
            {mutation.isPending ? "Creating…" : "Create"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
